<template>
    <div>
        <v-card class="mb-2 tw-font-normal" rounded="lg" flat>
            <v-card-text>
                <div
                    class="tw-flex tw-items-center tw-justify-between tw-w-full"
                >
                    <div class="tw-flex tw-items-center tw-flex-1">
                        <Avatar
                            :size="32"
                            :fullname="attendeeName"
                            :color="getColour(attendeeName)"
                        />
                        <div class="tw-ml-3">
                            <div class="tw-text-sm tw-font-bold">
                                {{ $t('message.classAttendee') }} :
                            </div>
                            <div>
                                {{ attendeeName }}
                            </div>
                        </div>
                    </div>
                    <div class="tw-flex tw-justify-end tw-items-center tw-px-4">
                        <div v-if="showSpotInfo" class="tw-text-center">
                            <div class="tw-text-sm tw-font-bold">
                                {{ $t('message.spot') }}
                            </div>
                            <div>{{ subItem.spotNumber }}</div>
                        </div>
                    </div>
                    <div
                        class="tw-flex tw-justify-center tw-items-center tw-text-center"
                    >
                        <div v-if="isCancelled">
                            <v-chip
                                v-if="!Boolean(subItem.refunded)"
                                small
                                color="error lighten-1"
                                class="tw-flex tw-items-center"
                            >
                                <v-icon size="14" class="tw-mr-1"
                                    >mdi-block-helper</v-icon
                                >
                                <span>{{ $t('message.notRefunded') }}</span>
                            </v-chip>

                            <v-chip
                                v-if="Boolean(subItem.refunded)"
                                small
                                color="success"
                                class="tw-flex tw-items-center"
                            >
                                <v-icon size="14" class="tw-mr-1"
                                    >mdi-check-all</v-icon
                                >
                                <span>{{ $t('message.refunded') }}</span>
                            </v-chip>
                        </div>
                        <div v-if="subItem.waitlistPosition" class="tw-px-4">
                            <div class="tw-text-sm tw-font-bold">
                                {{ $t('message.waitlist') }}
                            </div>
                            <div>
                                {{
                                    `${subItem.waitlistPosition} ${$t(
                                        'message.position'
                                    )}`
                                }}
                            </div>
                        </div>
                        <v-btn
                            v-if="showChangeAttendeeBtn"
                            small
                            depressed
                            color="grey lighten-3"
                            @click="openAssignSpotDialog"
                        >
                            <v-icon size="16" class="md:tw-mr-1 night:tw-text-neutral-800"
                                >mdi-pencil-outline</v-icon
                            >
                            <span class="tw-hidden md:tw-block night:tw-text-neutral-800">{{
                                $t('message.changeAttendee')
                            }}</span>
                        </v-btn>
                    </div>
                </div>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-text>
                <div
                    class="tw-flex tw-items-center tw-justify-between tw-flex-wrap"
                >
                    <div class="tw-w-full md:tw-w-1/6 tw-mb-2 md:tw-mb-0">
                        <div>
                            {{ subItem.timeStart }} - {{ subItem.timeEnd }}
                        </div>
                    </div>
                    <div class="tw-w-full md:tw-w-1/6 tw-mb-2 md:tw-mb-0">
                        <div>{{ subItem.name || subItem.activityName }}</div>
                        <div class="tw-text-xs tw-font-bold">
                            {{ $t('message.credits') }} : {{ numCredits }}
                        </div>
                    </div>
                    <div class="tw-w-3/6 md:tw-w-1/6 tw-mb-2 md:tw-mb-0">
                        <div>
                            <v-icon size="16" class="tw-mr-1 md:!tw-hidden"
                                >mdi-account-box-outline</v-icon
                            >{{ instructorNames }}
                        </div>
                    </div>
                    <div class="tw-w-3/6 md:tw-w-1/6 tw-mb-2 md:tw-mb-0">
                        <div>
                            <v-icon size="16" class="tw-mr-1 md:!tw-hidden"
                                >mdi-map-marker</v-icon
                            >{{ subItem.location }}
                        </div>
                    </div>
                    <div
                        class="tw-w-full md:tw-w-fit tw-flex tw-items-center tw-justify-end"
                    >
                        <div
                            class="tw-w-full tw-text-center tw-mt-2 md:tw-mb-0"
                            v-if="
                                cancelUpcomingBtn ||
                                cancelWaitlistBtn ||
                                isCancelled
                            "
                        >
                            <CancelBooking
                                v-if="cancelUpcomingBtn"
                                :subItems="[subItem]"
                                @success="cancelled"
                                @cancelFeePaymentError="
                                    (err) => $emit('cancelFeePaymentError', err)
                                "
                                redTextStyle
                            />
                            <RemoveClass
                                v-if="cancelWaitlistBtn"
                                :subItem="subItem"
                                @success="removed"
                                redTextStyle
                            />
                            <v-btn
                                v-if="isCancelled"
                                disabled
                                small
                                class="tw-w-full md:tw-w-fit"
                            >
                                <v-icon size="16" class="tw-mr-1"
                                    >mdi-close-circle-outline</v-icon
                                >{{ $t('message.cancelled') }}
                            </v-btn>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <AssignSpotDialog
            :isOpen="assignSpotDialog"
            :accounts="accounts"
            :selectedSpot="subItem"
            @close="assignSpotDialog = false"
            @refresh="refresh"
        />
    </div>
</template>

<script>
import CancelBooking from '@/components/myclasses/CancelBooking.vue'
import RemoveClass from '@/components/myclasses/RemoveClass.vue'
import AssignSpotDialog from '@/components/myclasses/AssignSpotDialog.vue'
import { AUTHENTICATION_MODULE_NAME } from '@/store/moduleNames'
import { mapGetters } from 'vuex'
import Avatar from 'vue-avatar-component'
import { getColour } from '@/util/profile'

export default {
    components: {
        AssignSpotDialog,
        RemoveClass,
        CancelBooking,
        Avatar,
    },
    props: {
        subItem: Object,
        isClickable: {
            type: Boolean,
            default: false,
        },
        bookBtn: {
            type: Boolean,
            default: true,
        },
        cancelWaitlistBtn: {
            type: Boolean,
            default: false,
        },
        cancelUpcomingBtn: {
            type: Boolean,
            default: false,
        },
        isMobileView: {
            type: Boolean,
            default: false,
        },
        changeAttendeeBtn: {
            type: Boolean,
            default: false,
        },
        accounts: {
            type: Array,
            default: [],
        },
    },
    data() {
        return {
            assignSpotDialog: false,
        }
    },
    computed: {
        ...mapGetters({
            user: `${AUTHENTICATION_MODULE_NAME}/getUser`,
        }),
        attendeeName() {
            return this.subItem.fullName || this.user.fullName
        },
        hideSpots() {
            return this.subItem.hideSpots === true
        },
        classLink() {
            const studio = this.$route.params.studioRoute
            const hash = this.$route.hash
            return this.subItem.courseOfferingId
                ? `/${studio}/courses/${this.subItem.courseOfferingId}${hash}`
                : `/${studio}/classes/${
                      this.subItem.classId || this.subItem.id
                  }${hash}`
        },
        numCredits() {
            if (this.subItem.numCredits) {
                return this.subItem.numCredits
            }
            if (this.subItem.numCreditsBooked) {
                return this.subItem.numCreditsBooked
            }
            return 1
        },
        checkIfBooked() {
            if (!this.user) {
                return false
            }
            return this.subItem.spots.some(
                (spot) => spot.username === this.user.username
            )
        },
        isFull() {
            return (
                this.subItem.isFull ||
                this.subItem.participantCount === this.subItem.capacity
            )
        },
        instructorNames() {
            return (
                this.subItem.instructors?.map((it) => it.name).join(', ') ||
                this.subItem.instructor?.name
            )
        },
        showCancelBtn() {
            return this.cancelUpcomingBtn || this.cancelWaitlistBtn
        },
        isCancelled() {
            return this.subItem.cancelTimestamp
        },
        showSpotInfo() {
            return this.subItem.spotNumber && !this.isCancelled
        },
        showChangeAttendeeBtn() {
            return (
                this.changeAttendeeBtn && Boolean(this.subItem.packageOwnerId)
            )
        },
    },
    methods: {
        getColour,
        cancelled() {
            this.$emit('cancelled')
        },
        assignFamilyUser() {
            this.$emit('assignFamilyUser')
        },
        removed() {
            this.$emit('removed')
        },
        openAssignSpotDialog() {
            this.assignSpotDialog = true
        },
        refresh() {
            this.$emit('refresh')
        },
    },
}
</script>
